import React, { useEffect, useState } from "react";
import { Package, Search, Eye, BarChart2, TrendingUp, TrendingDown, Minus } from "react-feather";
import guavaLogo from "../../../assets/images/guava-logo.png";
import "../../../assets/scss/pages/_guavaDashboard.scss";
import adminService from "../../../services/adminService";
import { useDispatch } from "react-redux";

// ------------------ TypeScript Interfaces ------------------
interface Product {
  _id: string;
  name: string;
  nutritionScore: number;
}

interface TopProduct {
  _id: string;
  count: number;
  product: Product;
}

interface Ingredient {
  rank: number;
  name: string;
  value: number;
  change: number;
}

interface Feedback {
  text: string;
  color: "success" | "danger" | "warning";
  value: number;
}

interface DashboardData {
  products: number;
  searches: number;
  impressions: number;
  avgPortfolioScore: number;
  topIngredients: Ingredient[]; // If API provides proper structure
  topProducts: TopProduct[];
  userFeedback?: Feedback[];
}

// ------------------ Stats Cards ------------------
const StatsCards: React.FC<{ data: DashboardData | null }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="row g-4 mb-4">
      <div className="col-md-3">
        <div className="card shadow-sm h-100">
          <div className="card-body text-center">
            <Package className="mb-2 text-primary" size={28} />
            <h5 className="fw-bold">{data.products}</h5>
            <p className="text-muted mb-0">Total Products</p>
          </div>
        </div>
      </div>

      <div className="col-md-3">
        <div className="card shadow-sm h-100">
          <div className="card-body text-center">
            <Search className="mb-2 text-success" size={28} />
            <h5 className="fw-bold">{data.searches}</h5>
            <p className="text-muted mb-0">Total Searches</p>
          </div>
        </div>
      </div>

      <div className="col-md-3">
        <div className="card shadow-sm h-100">
          <div className="card-body text-center">
            <Eye className="mb-2 text-purple" size={28} />
            <h5 className="fw-bold">{data.impressions}</h5>
            <p className="text-muted mb-0">Total Impressions</p>
          </div>
        </div>
      </div>

      <div className="col-md-3">
        <div className="card shadow-sm h-100">
          <div className="card-body text-center">
            <BarChart2 className="mb-2 text-warning" size={28} />
            <h5 className="fw-bold">{data.avgPortfolioScore}</h5>
            <p className="text-muted mb-0">Avg Portfolio Score</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------ Top Ingredients ------------------
// ------------------ Top Ingredients ------------------
const TopIngredients: React.FC<{ ingredients: Ingredient[] }> = ({ ingredients }) => {
  return (
    <div className="card shadow-sm h-100">
      <div className="card-header fw-semibold">Top Ingredients</div>
      <div className="card-body p-0">
        {ingredients.length === 0 ? (
          <div className="text-center text-muted py-3">Not available</div>
        ) : (
          <ul className="list-group list-group-flush">
            {ingredients.map((item) => (
              <li
                key={item.rank}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <span className="badge bg-primary me-2">{item.rank}</span>
                  {item.name}
                </div>
                <div className="d-flex align-items-center">
                  <span className="fw-semibold me-2">{item.value}</span>
                  {item.change > 0 ? (
                    <span className="text-success d-flex align-items-center">
                      <TrendingUp size={14} className="me-1" /> {item.change}%
                    </span>
                  ) : item.change < 0 ? (
                    <span className="text-danger d-flex align-items-center">
                      <TrendingDown size={14} className="me-1" /> {Math.abs(item.change)}%
                    </span>
                  ) : (
                    <span className="text-muted d-flex align-items-center">
                      <Minus size={14} className="me-1" /> 0%
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};


// ------------------ Top Products ------------------
const TopProducts: React.FC<{ products: TopProduct[] }> = ({ products }) => {
  return (
    <div className="card shadow-sm h-100">
      <div className="card-header fw-semibold">Top Products</div>
      <div className="card-body p-0">
        <ul className="list-group list-group-flush">
          {products.map((item, index) => (
            <li
              key={item._id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <span className="badge bg-purple me-2">S{index + 1}</span>
                {item.product?.name || "Unnamed Product"}
                <br />
                <small className="text-muted">{item.count} impressions</small>
              </div>
              <span className="text-muted">Score: {item.product?.nutritionScore}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ------------------ User Feedback ------------------
const UserFeedback: React.FC<{ feedback: Feedback[] }> = ({ feedback }) => {
  return (
    <div className="card shadow-sm">
      <div className="card-header fw-semibold">User Feedback Insights</div>
      <div className="card-body">
        {feedback.map((item, idx) => (
          <div key={idx} className="d-flex align-items-center justify-content-between mb-3">
            {/* Left side text */}
            <div className="d-flex align-items-center">
              <span
                className={`me-2 rounded-circle bg-${item.color}`}
                style={{ width: "8px", height: "8px", display: "inline-block" }}
              ></span>
              <span>{item.text}</span>
            </div>

            {/* Progress bar + percentage */}
            <div className="d-flex align-items-center" style={{ minWidth: "200px" }}>
              <div className="progress flex-grow-1 me-2" style={{ height: "6px" }}>
                <div
                  className={`progress-bar bg-${item.color}`}
                  role="progressbar"
                  style={{ width: `${item.value}%` }}
                  aria-valuenow={item.value}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
              <small className="text-muted fw-semibold">{item.value}%</small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------------------ Main Dashboard ------------------
const GuavaDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // useEffect(() => {
  //   const fetchDashboardData = async () => {
  //     try {
  //       const response = await fetch(
  //         "https://guava-apis-934140575807.us-central1.run.app/api/v1/admin/dashboard"
  //       );
  //       const json = await response.json();

  //       // Map API data to your UI structure
  //       const mappedData: DashboardData = {
  //         products: json.data.products || 0,
  //         searches: json.data.searches || 0,
  //         impressions: json.data.impressions || 0,
  //         avgPortfolioScore: json.data.avgPortfolioScore || 0,
  //         topIngredients: [], // Adjust if API provides topIngredients
  //         topProducts: json.data.topProducts || [],
  //         userFeedback: [
  //           { text: "Too expensive", color: "danger", value: 72 },
  //           { text: "Great ingredients", color: "success", value: 89 },
  //           { text: "Hard to find", color: "danger", value: 55 },
  //           { text: "Love the packaging", color: "success", value: 81 },
  //           { text: "Tastes amazing", color: "success", value: 88 }
  //         ]
  //       };

  //       setDashboardData(mappedData);
  //     } catch (error) {
  //       console.error("Error fetching dashboard data:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchDashboardData();
  // }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // call the new guava endpoint through dispatch
        const data: any = await dispatch<any>(adminService.getGuavaData());

        const mappedData: DashboardData = {
          products: data.data?.products || 0,
          searches: data.data?.searches || 0,
          impressions: data.data?.impressions || 0,
          avgPortfolioScore: data.data?.avgPortfolioScore || 0,
          topIngredients: data.data?.topIngredients || [],
          topProducts: data.data?.topProducts || [],
          userFeedback: [
            { text: "Too expensive", color: "danger", value: 72 },
            { text: "Great ingredients", color: "success", value: 89 },
            { text: "Hard to find", color: "danger", value: 55 },
            { text: "Love the packaging", color: "success", value: 81 },
            { text: "Tastes amazing", color: "success", value: 88 }
          ]
        };

        setDashboardData(mappedData);
      } catch (error) {
        console.error("Error fetching guava dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch]);

  if (loading) return <div className="text-center py-5">Loading...</div>;

  return (
    <div className="guava-dashboard container-fluid py-4" data-testid="guava-dashboard">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <img src={guavaLogo} alt="Guava Logo" className="dashboard-logo me-3" />
        <div>
          <h2 className="dashboard-title mb-1">Guava Dashboard</h2>
          <p className="text-muted mb-0">Consumer insights and product discovery analytics</p>
        </div>

        <div className="ms-auto d-flex align-items-center gap-3">
          <select className="form-select filter-select" data-testid="select-brands-guava">
            <option>All Brands</option>
          </select>
          <select className="form-select filter-select" data-testid="select-timeframe">
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
            <option>Last Year</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards data={dashboardData} />

      {/* Ingredients + Products */}
      <div className="row g-4 mt-2">
        <div className="col-lg-6">
          {dashboardData && <TopIngredients ingredients={dashboardData.topIngredients} />}
        </div>
        <div className="col-lg-6">
          {dashboardData && <TopProducts products={dashboardData.topProducts} />}
        </div>
      </div>

      {/* User Feedback */}
      <div className="mt-4">
        {dashboardData && dashboardData.userFeedback && (
          <UserFeedback feedback={dashboardData.userFeedback} />
        )}
      </div>
    </div>
  );
};

export default GuavaDashboard;
